import affirm from '../core/Affirm'
import { ArgumentKeyType, NodeType } from './Nodes'
import { NodeDataMap, NodeDryRunSettingsMap, NodeOutputMap } from './NodeTypes'

export type NodeGroup = 'datasource' | 'transformation' | 'control-flow' | 'variable' | 'development' | 'sub-flow' | 'artifact' | 'settlement'
export type TypeAcceptedData = 'sql' | 'json' | 'number' | 'string' | 'multiline' | 'select' | 'multi-select' | 'boolean' | 'js' | 'custom'

export interface IAcceptedData<T extends NodeType> {
    parameter: keyof NodeDataMap[T]
    isRequired: boolean
    type: TypeAcceptedData
    sourceHandle?: ArgumentKeyType<T>[]
    header?: string
    tooltipContent?: string
    placeholder?: string
    defaultValue?: any
    options?: {
        value: any
        label: string
    }[]
}

export interface IDryRunSettingsData<T extends NodeType> {
    parameter: keyof NodeDryRunSettingsMap[T]
    type: TypeAcceptedData
    options?: {
        value: any
        label: string
    }[]
}

export interface INodeDescription<T extends NodeType> {
    name: string
    type: NodeType
    icon: string
    group: NodeGroup
    description: string
    acceptedInputNodes: {
        nodeType: NodeType
    }[]
    acceptedInputs: {
        argumentKey: ArgumentKeyType<T>
        count: 'one' | 'zero-or-one' | 'one-or-more' | 'zero-or-more'
    }[]
    acceptedData: IAcceptedData<T>[]
    dryRunConfig?: IDryRunSettingsData<T>[]
    outputs: {
        key: keyof NodeOutputMap[T]
        label?: string
    }[]
}

const startDescription: INodeDescription<'start'> = {
    name: 'Start',
    icon: 'start',
    group: 'control-flow',
    type: 'start',
    description: '',
    acceptedInputNodes: [],
    acceptedInputs: [],
    acceptedData: [],
    outputs: [
        { key: 'timestamp' }
    ]
}

const dynamoNodeDescription: INodeDescription<'ds_dynamo'> = {
    name: 'DynamoDB',
    icon: 'dynamo',
    group: 'datasource',
    type: 'ds_dynamo',
    description: '',
    acceptedInputNodes: [],
    acceptedInputs: [],
    acceptedData: [],
    outputs: [
        { key: 'datasource' }
    ]
}

const redshiftDescription: INodeDescription<'ds_redshift'> = {
    name: 'Redshift',
    icon: 'redshift',
    group: 'datasource',
    type: 'ds_redshift',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'start' }
    ],
    acceptedInputs: [
        { argumentKey: 'timestamp', count: 'zero-or-one' }
    ],
    acceptedData: [],
    outputs: [
        { key: 'datasource' }
    ]
}

const contractNodeDescription: INodeDescription<'ds_contract'> = {
    name: 'Contract',
    icon: 'contract',
    group: 'datasource',
    type: 'ds_contract',
    description: '',
    acceptedInputNodes: [],
    acceptedInputs: [],
    acceptedData: [
        { parameter: 'contractData', isRequired: true, type: 'custom' }
    ],
    outputs: [
        { key: 'result' }
    ]
}

const sqlNodeDescription: INodeDescription<'xfrm_sql'> = {
    name: 'SQL',
    icon: 'sql',
    group: 'transformation',
    type: 'xfrm_sql',
    description: '',
    dryRunConfig: [
        { type: 'number', parameter: 'samplePercentage' }
    ],
    acceptedInputNodes: [
        { nodeType: 'ds_dynamo' },
        { nodeType: 'ds_redshift' },
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'datasource', count: 'one' },
        { argumentKey: 'parameter', count: 'zero-or-more' }
    ],
    acceptedData: [
        { parameter: 'text', isRequired: true, type: 'sql', header: 'Insert SQL query' }
    ],
    outputs: [
        { key: 'items' }
    ]
}

const codeDescription: INodeDescription<'xfrm_code'> = {
    name: 'Code',
    icon: 'code',
    group: 'transformation',
    type: 'xfrm_code',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'start' },
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'data', count: 'zero-or-more' }
    ],
    acceptedData: [
        {
            parameter: 'source',
            isRequired: true,
            type: 'js',
            defaultValue: `function run(globals, inputValues) {
    const result = {}
    // your code here...
    return result
}`,
            header: 'Insert code snippet',
            sourceHandle: ['data']
        }
    ],
    outputs: [
        { key: 'result' }
    ]
}

const filterDescription: INodeDescription<'xfrm_filterby'> = {
    name: 'Filter By',
    icon: 'filter',
    group: 'transformation',
    type: 'xfrm_filterby',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'items', count: 'one' },
        { argumentKey: 'filter', count: 'zero-or-one' }
    ],
    acceptedData: [
        { parameter: 'filterData', isRequired: true, type: 'custom' }
    ],
    outputs: [
        { key: 'result' }
    ]
}

const orderDescription: INodeDescription<'xfrm_orderby'> = {
    name: 'Order By',
    icon: 'order',
    group: 'transformation',
    type: 'xfrm_orderby',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'items', count: 'one-or-more' }],
    acceptedData: [
        { parameter: 'itemKey', isRequired: true, type: 'string', header: 'Choose the key', tooltipContent: 'The field to sort by', sourceHandle: ['items'] },
        { parameter: 'sortType', isRequired: true, type: 'select', header: 'Choose the sort type (asc or desc)', tooltipContent: 'The sort type', sourceHandle: ['items'], options: [
            { label: 'Ascending', value: 'asc' },
            { label: 'Descending', value: 'desc' }
        ] }
    ],
    outputs: [
        { key: 'result' }
    ]
}

const groupDescription: INodeDescription<'xfrm_groupby'> = {
    name: 'Group By',
    icon: 'group',
    group: 'transformation',
    type: 'xfrm_groupby',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'items', count: 'one-or-more' }],
    acceptedData: [
        {
            parameter: 'itemKey',
            isRequired: true,
            type: 'string',
            header: 'Choose the key',
            tooltipContent: 'The field to group records by, creating collections of related data',
            sourceHandle: ['items']
        }
    ],
    outputs: [
        { key: 'result' },
        { key: 'keys' }
    ]
}

const dataframeReduceDescription: INodeDescription<'xfrm_reduce'> = {
    name: 'Dataframe Reduce',
    icon: 'reduce',
    group: 'transformation',
    type: 'xfrm_reduce',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'items', count: 'one' }],
    acceptedData: [
        { parameter: 'operations', isRequired: true, type: 'select', header: 'Operations', tooltipContent: `You can perform the follow operations:\nSum: Adds all values together\nMean: Calculates the average of values\nFirst: Takes the first value in the series\nLast: Takes the last value in the series\nMin: Finds the minimum value\nMax: Finds the maximum value`,
            sourceHandle: ['items'], options: [
                { label: 'Addition', value: 'sum' },
                { label: 'Average', value: 'mean' },
                { label: 'First element', value: 'first' },
                { label: 'Last element', value: 'last' },
                { label: 'Minimum', value: 'min' },
                { label: 'Maximum', value: 'max' }
            ]
        }
    ],
    outputs: [
        { key: 'result' }
    ]
}
const dataframeOpsDescription: INodeDescription<'xfrm_dataframeops'> = {
    name: 'Dataframe Ops',
    icon: 'dataframeOps',
    group: 'transformation',
    type: 'xfrm_dataframeops',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'df1', count: 'one' },
        { argumentKey: 'df2', count: 'one' }
    ],
    acceptedData: [
        { parameter: 'operations', isRequired: true, type: 'select', header: 'Operations', options: [
            { label: 'Addition', value: 'add' },
            { label: 'Substraction', value: 'sub' },
            { label: 'Multiplication', value: 'mul' },
            { label: 'Division', value: 'div' },
            { label: 'Average', value: 'avg' },
            { label: 'Percentage', value: 'perc' },
            { label: 'Minimum', value: 'capAt' },
            { label: 'Maximum', value: 'maxAt' }
        ]
        }
    ],
    outputs: [
        { key: 'result' }
    ]
}

const mergeDescription: INodeDescription<'xfrm_merge'> = {
    name: 'Merge',
    icon: 'merge',
    group: 'transformation',
    type: 'xfrm_merge',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'xfrm_code' },
        { nodeType: 'xfrm_caseWhen' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'left', count: 'one' },
        { argumentKey: 'right', count: 'one' }
    ],
    acceptedData: [{ parameter: 'mergeData', isRequired: true, type: 'custom' }],
    outputs: [
        { key: 'result' }
    ]
}

const operationsDescription: INodeDescription<'xfrm_operations'> = {
    name: 'Operations',
    icon: 'operations',
    group: 'transformation',
    type: 'xfrm_operations',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'xfrm_code' },
        { nodeType: 'xfrm_caseWhen' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'data', count: 'one' }
    ],
    acceptedData: [{ parameter: 'operationsData', isRequired: true, type: 'custom' }],
    outputs: [
        { key: 'result' }
    ]
}

const aggregateByDescription: INodeDescription<'xfrm_aggregateBy'> = {
    name: 'Aggregate by',
    icon: 'aggregateBy',
    group: 'transformation',
    type: 'xfrm_aggregateBy',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'data', count: 'one' }
    ],
    acceptedData: [{ parameter: 'groupingData', isRequired: true, type: 'custom' }],
    outputs: [
        { key: 'result' }
    ]
}

const variableDescription: INodeDescription<'var_variable'> = {
    name: 'Variable',
    icon: 'variable',
    group: 'variable',
    type: 'var_variable',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'start' },
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'out_report' },
        { nodeType: 'xfrm_code' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'value', count: 'zero-or-more' }
    ],
    acceptedData: [
        { parameter: 'item', isRequired: false, type: 'custom' }
    ],
    outputs: [
        { key: 'value' }
    ]
}

const dataframeDescription: INodeDescription<'var_dataframe'> = {
    name: 'Dataframe',
    icon: 'dataframe',
    group: 'variable',
    type: 'var_dataframe',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'items', count: 'one' }
    ],
    acceptedData: [
        { parameter: 'xField', isRequired: true, type: 'string', header: 'Choose dataframe x-axis', sourceHandle: ['items'] },
        { parameter: 'yField', isRequired: true, type: 'string', header: 'Choose dataframe y-axis', sourceHandle: ['items'] },
        { parameter: 'collapse', isRequired: false, type: 'boolean' },
        { parameter: 'fill', isRequired: false, type: 'custom' }
    ],
    outputs: [
        { key: 'result' }
    ]
}

const executeFlowDescription: INodeDescription<'exe_subFlow'> = {
    name: 'Execute Sub Flow',
    icon: 'exe_subFlow',
    group: 'control-flow',
    type: 'exe_subFlow',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'xfrm_code' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'data', count: 'zero-or-more' }
    ],
    acceptedData: [
        { parameter: 'flowId', isRequired: true, type: 'custom' }
    ],
    outputs: [
        { key: 'result' }
    ]
}

const subFlowStartDescription: INodeDescription<'subFlow_start'> = {
    name: 'SubFlow Start',
    icon: 'subFlow_start',
    group: 'sub-flow',
    type: 'subFlow_start',
    description: '',
    acceptedInputNodes: [],
    acceptedInputs: [],
    acceptedData: [],
    outputs: [
        { key: 'data' }
    ]
}

const subFlowEndDescription: INodeDescription<'subFlow_end'> = {
    name: 'SubFlow End',
    icon: 'subFlow_end',
    group: 'sub-flow',
    type: 'subFlow_end',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'rtg_if' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'data', count: 'one' }
    ],
    acceptedData: [],
    outputs: []
}

const forkDescription: INodeDescription<'fork'> = {
    name: 'Fork',
    icon: 'fork',
    group: 'control-flow',
    type: 'fork',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'data', count: 'one-or-more' },
        { argumentKey: 'conditionData', count: 'zero-or-more' }
    ],
    acceptedData: [
        { parameter: 'condition', isRequired: true, type: 'custom' }
    ],
    outputs: [
        { key: 'false' },
        { key: 'true' }
    ]
}

const endForkDescription: INodeDescription<'end_fork'> = {
    name: 'End Fork',
    icon: 'end_fork',
    group: 'control-flow',
    type: 'end_fork',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'left', count: 'one' },
        { argumentKey: 'right', count: 'one' }
    ],
    acceptedData: [],
    outputs: [
        { key: 'data' }
    ]
}

const dummyDescription: INodeDescription<'dummy'> = {
    name: 'Dummy',
    icon: 'dummy',
    group: 'development',
    type: 'dummy',
    description: '',
    acceptedInputNodes: [],
    acceptedInputs: [
        { argumentKey: 'value', count: 'zero-or-more' }
    ],
    acceptedData: [
        { parameter: 'val', isRequired: true, type: 'number' }
    ],
    outputs: [
        { key: 'value' }
    ]
}

const dummy2Description: INodeDescription<'dummy2'> = {
    name: 'Dummy 2',
    icon: 'dummy',
    group: 'development',
    type: 'dummy2',
    description: '',
    acceptedInputNodes: [],
    acceptedInputs: [
        { argumentKey: 'val2', count: 'zero-or-more' }
    ],
    acceptedData: [
        { parameter: 'val', isRequired: true, type: 'string' }
    ],
    outputs: [
        { key: 'val3' }
    ]
}

const csvArtifactDescription: INodeDescription<'out_csv'> = {
    name: 'CSV export',
    icon: 'csv',
    group: 'artifact',
    type: 'out_csv',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'items', count: 'one' }
    ],
    acceptedData: [{ type: 'string', parameter: 'csvName', header: 'Insert the csv name', isRequired: false }],
    outputs: [
        { key: 'fileKey' }
    ]
}

const reportArtifactDescription: INodeDescription<'out_report'> = {
    name: 'Report export',
    icon: 'report',
    group: 'artifact',
    type: 'out_report',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'xfrm_code' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'data', count: 'one-or-more' }
    ],
    acceptedData: [{ parameter: 'reportData', isRequired: true, type: 'custom' }],
    outputs: [
        { key: 'fileKey' }
    ]
}

export const settlementDescription: INodeDescription<'stmt_settlement'> = {
    name: 'Settlement Result',
    icon: 'settlement',
    group: 'settlement',
    type: 'stmt_settlement',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'value', count: 'one-or-more' }
    ],
    acceptedData: [
        { parameter: 'itemsMap', isRequired: true, type: 'custom' }
    ],
    outputs: [
        { key: 'result' }
    ]
}

export const pivotDescription: INodeDescription<'xfrm_pivot'> = {
    name: 'Pivot',
    icon: 'pivot',
    group: 'transformation',
    type: 'xfrm_pivot',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'data', count: 'one' }
    ],
    acceptedData: [
        { parameter: 'itemKey', isRequired: true, type: 'string', sourceHandle: ['data'], header: 'Pivot Key' },
        { parameter: 'nameKeyResult', isRequired: false, type: 'string', defaultValue: 'field', header: 'Pivot column name', placeholder: 'field' }
    ],
    outputs: [
        { key: 'result' }
    ]
}

export const caseWhenDescription: INodeDescription<'xfrm_caseWhen'> = {
    name: 'Case When',
    icon: 'caseWhen',
    group: 'transformation',
    type: 'xfrm_caseWhen',
    description: '',
    acceptedInputNodes: [
        { nodeType: 'var_variable' },
        { nodeType: 'fork' },
        { nodeType: 'end_fork' },
        { nodeType: 'exe_subFlow' },
        { nodeType: 'subFlow_end' },
        { nodeType: 'subFlow_start' },
        { nodeType: 'xfrm_aggregateBy' },
        { nodeType: 'xfrm_pivot' },
        { nodeType: 'xfrm_filterby' },
        { nodeType: 'xfrm_groupby' },
        { nodeType: 'xfrm_orderby' },
        { nodeType: 'xfrm_merge' },
        { nodeType: 'xfrm_operations' },
        { nodeType: 'xfrm_sql' },
        { nodeType: 'out_csv' },
        { nodeType: 'var_dataframe' },
        { nodeType: 'xfrm_reduce' },
        { nodeType: 'ds_contract' }
    ],
    acceptedInputs: [
        { argumentKey: 'data', count: 'one' }
    ],
    acceptedData: [
        { parameter: 'caseWhenData', isRequired: true, type: 'custom' }
    ],
    outputs: [
        { key: 'result' }
    ]
}

export const ALL_NODES_DESCRIPTION = [
    startDescription,
    dynamoNodeDescription,
    redshiftDescription,
    sqlNodeDescription,
    contractNodeDescription,
    codeDescription,
    filterDescription,
    orderDescription,
    groupDescription,
    dataframeReduceDescription,
    dataframeOpsDescription,
    mergeDescription,
    operationsDescription,
    aggregateByDescription,
    executeFlowDescription,
    variableDescription,
    dataframeDescription,
    subFlowStartDescription,
    subFlowEndDescription,
    forkDescription,
    endForkDescription,
    dummyDescription,
    dummy2Description,
    csvArtifactDescription,
    reportArtifactDescription,
    settlementDescription,
    pivotDescription,
    caseWhenDescription
]

// Validation of constants to ensure a correct system configuration
for (let i = 0; i < ALL_NODES_DESCRIPTION.length; i++) {
    const desc = ALL_NODES_DESCRIPTION[i]
    // I'm checking for '.' inside inputs and outputs and making sure there are none.
    // Cause if there are any, they can mess with the SQL placeholders and break the app.
    desc.acceptedInputs.forEach(input => {
        affirm(!(input.argumentKey as string).includes('.'), `Invalid token "." found in argument key "${input.argumentKey}" of node descriptor "${desc.name}"`)
    })
    desc.outputs.forEach(output => {
        affirm(!(output.key as string).includes('.'), `Invalid token "." found in output "${output.key}" of node descriptor "${desc.name}"`)
    })
}
